import { Button, Dropdown, IconGraphy, Input } from '@leapeasy/ui-kit';
import { Box } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { saveProfileAction } from 'store/actions/authActions';
import { isProfileEditFormValid, shouldShowProfilePhoneError } from 'utils/profileValidation';
import { DEFAULT_PROFILE_AVATAR } from 'data/images/avatars';

const MFTList = [
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
  { value: 'voice', label: 'Voice' },
];

export const ProfileEdit = ({
  closeDialog,
  onComplete,
  onResetPassword,
  name,
  phone_number,
  email,
  image,
  mfaMethod,
  jobTitle,
  variant = 'edit',
}) => {
  const isSetup2FA = variant === 'setup2fa';

  const [nameState, setNameState] = useState(name || '');
  const [phoneState, setPhoneState] = useState(phone_number || '');
  const [emailState, setEmailState] = useState(email || '');
  const [imageState, setImageState] = useState(image || DEFAULT_PROFILE_AVATAR);
  const [imageFileState, setImageFileState] = useState(image || '');
  const [editImageState, setEditImageState] = useState(false);
  const [mfaMethodState, setMfaMethodState] = useState(mfaMethod || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const fileInputRef = useRef(null);
  const previewUrlRef = useRef(null);
  const previousLoadingRef = useRef(false);
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.getIn(['auth', 'loading']));
  const error = useSelector((state) => state.getIn(['auth', 'error']));

  const handleStateChange = (field, value) => {
    if (field === 'name') setNameState(value);
    if (field === 'phone_number') setPhoneState(value);
    if (field === 'email') setEmailState(value);
    if (field === 'mfaMethod') setMfaMethodState(value);
  };

  const handleAvatarUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      event.target.value = '';
      return;
    }

    const imagePreviewUrl = URL.createObjectURL(file);
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    previewUrlRef.current = imagePreviewUrl;
    setImageState(imagePreviewUrl);
    setImageFileState(file);
    setEditImageState(true);
    event.target.value = '';
  };

  const needsCurrentPassword = useMemo(() => {
    const phoneDigits = (value) => {
      const digits = String(value || '').replace(/\D/g, '');
      if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
      return digits;
    };
    const emailChanged =
      emailState.trim().toLowerCase() !==
      String(email || '')
        .trim()
        .toLowerCase();
    const phoneChanged = phoneDigits(phoneState) !== phoneDigits(phone_number);
    const mfaChanged = String(mfaMethodState || '') !== String(mfaMethod || '');
    return emailChanged || phoneChanged || mfaChanged;
  }, [emailState, email, phoneState, phone_number, mfaMethodState, mfaMethod]);

  const isFormValid = useMemo(
    () =>
      isProfileEditFormValid({
        name: nameState,
        email: emailState,
        phone: phoneState,
        mfaMethod: mfaMethodState,
        isSetup2FA,
      }) &&
      (!needsCurrentPassword || currentPassword.trim()),
    [
      nameState,
      emailState,
      phoneState,
      mfaMethodState,
      isSetup2FA,
      needsCurrentPassword,
      currentPassword,
    ],
  );

  const showPhoneError = useMemo(
    () => shouldShowProfilePhoneError(phoneState, mfaMethodState),
    [phoneState, mfaMethodState],
  );

  const onClickSaveEdits = () => {
    if (!isFormValid) return;

    const formData = new FormData();
    if (editImageState && imageFileState instanceof File) {
      formData.append('image', imageFileState);
    }
    formData.append('name', nameState);
    formData.append('phone', phoneState);
    formData.append('email', emailState);
    formData.append('job_title', jobTitle || '');
    formData.append('old_pwd', needsCurrentPassword ? currentPassword : '');
    formData.append('new_pwd', '');
    formData.append('edit_img', editImageState);
    formData.append('edit_pwd', false);
    formData.append('mfa_method', mfaMethodState);

    setHasAttemptedSave(true);
    dispatch(saveProfileAction(formData));
  };

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const saveJustFinished = previousLoadingRef.current && !loading;
    if (saveJustFinished && hasAttemptedSave && !error) {
      setHasAttemptedSave(false);
      if (isSetup2FA && onComplete) {
        onComplete();
      } else if (closeDialog) {
        closeDialog();
      }
    }
    previousLoadingRef.current = loading;
  }, [loading, error, hasAttemptedSave, closeDialog, isSetup2FA, onComplete]);

  return (
    <Container>
      <Header>
        <Text1>{isSetup2FA ? "Let's setup 2-factor authentication" : 'Edit Profile'}</Text1>

        {!isSetup2FA && closeDialog ? (
          <CloseButton onClick={closeDialog} aria-label="Close edit profile">
            <IconGraphy icon={'General.Cancel'} />
          </CloseButton>
        ) : null}
      </Header>

      {!isSetup2FA ? (
        <AvatarSection>
          <AvatarBox>
            <img
              src={imageState || DEFAULT_PROFILE_AVATAR}
              alt="User avatar"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = DEFAULT_PROFILE_AVATAR;
              }}
            />
            <AvatarUploadButton
              type="button"
              onClick={handleAvatarUploadClick}
              aria-label="Upload avatar"
            >
              <IconGraphy icon="MediaDevices.CameraAlt" width="20px" height="20px" />
            </AvatarUploadButton>
          </AvatarBox>
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarFileChange}
          />
        </AvatarSection>
      ) : null}

      <FormFields>
        <StyledInput>
          <Input
            icon={<IconGraphy icon="Users.User" />}
            label="Name"
            onChange={(e) => handleStateChange('name', e.target.value)}
            placeholder="Name"
            value={nameState}
            size={'medium'}
            fullWidth={true}
          />
        </StyledInput>

        <StyledInput>
          <Input
            icon={<IconGraphy icon="MediaDevices.Call" />}
            label="Phone Number"
            onChange={(e) => handleStateChange('phone_number', e.target.value)}
            placeholder="Phone Number"
            value={phoneState}
            type="tel"
            phoneCountry="US"
            size="medium"
            fullWidth
            errorType={showPhoneError ? 'error' : 'none'}
            helperMessage={
              showPhoneError
                ? 'Enter a valid US phone number (required for SMS and Voice 2FA).'
                : ''
            }
            showHelperOnTyping={false}
            showHelperMessage={showPhoneError}
          />
        </StyledInput>

        <StyledInput>
          <Input
            icon={<IconGraphy icon="Notifications.Email" />}
            label="Email"
            placeholder="Email"
            value={emailState}
            size={'medium'}
            fullWidth={true}
            disabled
          />
        </StyledInput>

        <StyledDropdown>
          <Dropdown
            icon={
              <IconGraphy color="#3C393D" height={20} icon="HomePlaces.MapsHomeWork" width={20} />
            }
            label="2FA Method"
            size="medium"
            value={mfaMethodState}
            options={MFTList.map((method) => ({
              text: method.label,
              value: method.value,
            }))}
            placeholder="Select MFA Method"
            onChange={(e) => handleStateChange('mfaMethod', e.target.value)}
            showSearch={false}
          />
        </StyledDropdown>

        {needsCurrentPassword ? (
          <StyledInput>
            <Input
              icon={<IconGraphy icon="Security.Lock" />}
              label="Current password"
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Current password"
              value={currentPassword}
              type="password"
              autoComplete="current-password"
              size="medium"
              fullWidth
            />
          </StyledInput>
        ) : null}

        <StyledInput>
          <Input
            icon={<IconGraphy icon="Business.Manufacturing" />}
            label="Job Title"
            placeholder="Job Title"
            value={jobTitle}
            size={'medium'}
            fullWidth={true}
            disabled
          />
        </StyledInput>
      </FormFields>

      <SaveButton
        variant="primary"
        size="medium"
        onClick={onClickSaveEdits}
        disabled={loading || !isFormValid}
      >
        {loading ? 'Saving...' : isSetup2FA ? 'Save and continue' : 'Save Edits'}
      </SaveButton>

      {hasAttemptedSave && error && <ErrorText>{error}</ErrorText>}

      {!isSetup2FA && onResetPassword ? (
        <ResetPasswordButton variant="tertiary" size="medium" onClick={onResetPassword}>
          Reset Password
        </ResetPasswordButton>
      ) : null}
    </Container>
  );
};

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  border-bottom: 1px solid #eaeaea;
`;

const AvatarSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
`;

const Text1 = styled.p`
  font-family: Stolzl;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%; /* 28px */
  color: #2e0f40;
`;

const FormFields = styled.div`
  display: flex;
  flex-direction: column;
`;

const CloseButton = styled.button`
  background: transparent;
  outline: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: #2e0f40;
  width: 32px;
  height: 32px;

  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: #2e0f40;
    width: 20px;
    height: 20px;
  }
`;

const AvatarBox = styled.div`
  width: 110px;
  height: 110px;
  border-radius: 50%;
  background-color: #f8f4f9;
  display: flex;
  align-items: center;
  justify-content: center;

  position: relative;

  & > img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }
`;

const AvatarUploadButton = styled.button`
  position: absolute;
  bottom: -10px;
  right: -5px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e8d6eb;
  cursor: pointer;
  border: none;
  padding: 0;

  & > svg {
    color: #90388b;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const StyledInput = styled.div`
  & > div:nth-child(1) {
    & > div:nth-child(2) {
      & > div:nth-child(1) {
        width: 100%;
      }
    }
  }
`;

const StyledDropdown = styled.div`
  & > div:nth-child(1) {
    width: 100%;
  }
`;

const SaveButton = styled(Button)`
  width: 100%;
  min-height: 40px;
`;

const ErrorText = styled.p`
  margin: 8px 0 0;
  color: #d92d20;
  font-family: Inter;
  font-size: 12px;
  line-height: 1.4;
`;
const ResetPasswordButton = styled(Button)`
  margin-top: 16px;
  width: 100%;
  min-height: 40px;
  border: 1px solid #e8d6eb;
  background: #fff;
  color: #90388b;
`;
